import React, { useEffect, useRef, useState } from "react";

import { Formik } from "formik";
import { Link } from "react-router";

import {
  TwoFactorSessionError,
  TwoFactorSessionSetup,
  completeTwoFactorSessionSetup,
  getTwoFactorSessionSetup,
  getTwoFactorSessionStatus,
  verifyTwoFactorSession,
} from "./two-factor-session-client";
import { stampTwoFactor } from "../../auth-service-client";
import { redirectUser } from "../../authentication";
import { getAuthServiceRedirectTo } from "../../LoginWithLink/getRedirectLink";
import {
  AuthenticateIntro,
  CantFindCodeHint,
  ConfirmButton,
  TrustThisDeviceField,
} from "../AuthenticateMultifactorAuth/AuthenticateMultifactorAuth";
import {
  OTP_ERROR_COPY,
  OtpCodeField,
  otpCodeValidation,
} from "../Components/OtpCodeForm/OtpCodeForm";
import { SetupInstructions } from "../Components/SetupInstructions/SetupInstructions";
import {
  EnterCodeStep,
  SetupConfirmButton,
} from "../ConnectMultifactorAuth/ConnectMultifactorAuth";
import styles from "../MultifactorAuth.module.css";

const SETUP_EXPIRED = "Your setup session expired. Scan the new code.";

type Step =
  | { kind: "loading" }
  | { kind: "authenticate" }
  | { kind: "setup"; setup: TwoFactorSessionSetup }
  | { kind: "failed" };

type UserError = { message: string; loginButton: boolean };

type FormValues = { otpCode: string; remember_device: boolean };

const GENERIC_ERROR: UserError = {
  message: OTP_ERROR_COPY.generic,
  loginButton: true,
};

const errorCode = (error: unknown) =>
  (error as TwoFactorSessionError | undefined)?.code;

const stampAndRedirect = async (assertion: string) => {
  await stampTwoFactor(assertion);
  await redirectUser({ redirectUrl: getAuthServiceRedirectTo() });
};

export const StepUpMultifactorAuth = () => {
  const [step, setStep] = useState<Step>({ kind: "loading" });
  const [userError, setUserError] = useState<UserError | null>(null);
  const started = useRef(false);

  const fail = (error: UserError = GENERIC_ERROR) => {
    setUserError(error);
    setStep({ kind: "failed" });
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    (async () => {
      let status;
      try {
        status = await getTwoFactorSessionStatus();
      } catch (error) {
        if ((error as TwoFactorSessionError | undefined)?.status === 401) {
          await redirectUser({ redirectUrl: "/login" });
          return;
        }
        fail();
        return;
      }

      try {
        if (status.device_remembered) {
          await stampAndRedirect(await verifyTwoFactorSession({}));
          return;
        }
        if (status.has_device) {
          setStep({ kind: "authenticate" });
          return;
        }
        if (!status.required) {
          await redirectUser({ redirectUrl: getAuthServiceRedirectTo() });
          return;
        }
        setStep({ kind: "setup", setup: await getTwoFactorSessionSetup() });
      } catch (error) {
        fail(
          errorCode(error) === "TwoFactorSetupAlreadyCompleted"
            ? {
                message: OTP_ERROR_COPY.setupAlreadyCompleted,
                loginButton: true,
              }
            : GENERIC_ERROR,
        );
      }
    })();
  }, []);

  const submit = async (values: FormValues) => {
    setUserError(null);

    let assertion: string;
    try {
      assertion =
        step.kind === "setup"
          ? await completeTwoFactorSessionSetup({
              setup_token: step.setup.setup_token,
              otp: values.otpCode,
            })
          : await verifyTwoFactorSession({
              otp: values.otpCode,
              remember_device: values.remember_device || false,
            });
    } catch (error) {
      const code = errorCode(error);

      if (code === "InvalidOTP") {
        setUserError({
          message: OTP_ERROR_COPY.invalidOtp,
          loginButton: false,
        });
        return;
      }

      if (code === "TokenExpired" && step.kind === "setup") {
        try {
          setStep({ kind: "setup", setup: await getTwoFactorSessionSetup() });
          setUserError({ message: SETUP_EXPIRED, loginButton: false });
        } catch {
          fail();
        }
        return;
      }

      if (code === "TwoFactorSetupAlreadyCompleted") {
        setUserError({
          message: OTP_ERROR_COPY.setupAlreadyCompleted,
          loginButton: true,
        });
        return;
      }

      setUserError(GENERIC_ERROR);
      return;
    }

    try {
      await stampAndRedirect(assertion);
    } catch {
      setUserError(GENERIC_ERROR);
    }
  };

  if (step.kind === "loading") return null;

  const clearError = () => {
    if (userError) setUserError(null);
  };

  const errorBlock = (
    <>
      {userError && (
        <p className={styles.validationError}>{userError.message}</p>
      )}
      {userError?.loginButton && (
        <Link to="/login" className={styles.link}>
          <p>Click here to log in</p>
        </Link>
      )}
    </>
  );

  if (step.kind === "failed") {
    return <div className={styles.modalLarge}>{errorBlock}</div>;
  }

  const form = (
    fields: (isSubmitting: boolean) => React.ReactNode,
    key?: string,
  ) => (
    <Formik<FormValues>
      key={key}
      initialValues={{ otpCode: "", remember_device: false }}
      onSubmit={submit}
      validationSchema={otpCodeValidation}
      validateOnMount={true}
    >
      {({ handleSubmit, isSubmitting }) => (
        <form onSubmit={handleSubmit}>
          {fields(isSubmitting)}
          {errorBlock}
        </form>
      )}
    </Formik>
  );

  if (step.kind === "setup") {
    return (
      <div className={styles.modalLarge}>
        <SetupInstructions
          setupUri={step.setup.setup_uri}
          account={step.setup.account}
          setupKey={step.setup.key}
        >
          {form(
            (isSubmitting) => (
              <>
                <EnterCodeStep
                  account={step.setup.account}
                  onClick={clearError}
                />
                <SetupConfirmButton disabled={isSubmitting} />
              </>
            ),
            step.setup.setup_token,
          )}
        </SetupInstructions>
      </div>
    );
  }

  return (
    <div className={styles.modalLarge}>
      <AuthenticateIntro />
      {form((isSubmitting) => (
        <>
          <OtpCodeField onClick={clearError} name="otpCode" />
          <TrustThisDeviceField />
          <ConfirmButton disabled={isSubmitting} />
        </>
      ))}
      <CantFindCodeHint />
    </div>
  );
};
